import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { estimateSessionCostUsd } from '@src/components/sco/Advisors/utils/sessionFormat';
import { ADVISOR_SESSION_BOOK_EXPERT } from '@src/gql/queries/AdvisorSessionBookExpert';
import { ScoAdvisorProfile, ScoAdvisorSessionPricing } from '@src/graphql-types';

/** What the booking form needs to know about the expert. */
export type SessionBookExpert = {
  name: string;
  photoUrl?: string | null;
  /** USD per hour. */
  hourlyRate: number | null;
  /** Server-priced packages (minutes → USD), the same numbers `quoteSession` charges. */
  packages: Array<{
    minutes: number;
    priceUsd: number;
  }>;
  defaultMinutes?: number | null;
  /** False when the viewer can't see or book this advisor (pricing is null). */
  acceptsSessions: boolean;
  timezone?: string | null;
  sessionWeeklyHours?: unknown;
};
export type SessionBookExpertData = {
  advisorSessionPricing?: ScoAdvisorSessionPricing | null;
  advisorProfile?: ScoAdvisorProfile | null;
};
export const toSessionBookExpert = (data?: SessionBookExpertData | null): SessionBookExpert | null => {
  const pricing = data?.advisorSessionPricing;
  const profile = data?.advisorProfile;
  if (!pricing && !profile) {
    return null;
  }
  return {
    name: profile?.user?.displayName || profile?.user?.fullName || 'this advisor',
    photoUrl: profile?.user?.pictureUrl,
    hourlyRate: pricing ? Number(pricing.hourlyRate) : null,
    packages: (pricing?.packages || []).filter(Boolean).map(p => ({
      minutes: Number(p.minutes),
      priceUsd: Number(p.priceUsd)
    })),
    defaultMinutes: pricing?.defaultMinutes ?? null,
    // Pricing is null unless the advisor is approved, accepting sessions, and visible to this viewer.
    acceptsSessions: !!pricing,
    timezone: profile?.timezone,
    sessionWeeklyHours: profile?.sessionWeeklyHours
  };
};

/** Package price from the server list; a custom length is hourlyRate × minutes / 60. */
export const sessionPriceUsd = (expert: SessionBookExpert, minutes: number): number | null => {
  const preset = expert.packages.find(p => p.minutes === minutes);
  if (preset) {
    return preset.priceUsd;
  }
  if (expert.hourlyRate == null || !Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }
  return estimateSessionCostUsd(expert.hourlyRate, minutes);
};

/**
 * The single place the booking form reads the expert's packages / rate
 * (`advisorSessionPricing`, readable by guests and members alike).
 */
export const useSessionBookExpert = expertUserId => {
  const $ = _c(11);
  let t0;
  if ($[0] !== expertUserId) {
    t0 = {
      userId: expertUserId
    };
    $[0] = expertUserId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = !expertUserId;
  let t2;
  if ($[2] !== t0 || $[3] !== t1) {
    t2 = {
      variables: t0,
      skip: t1,
      errorPolicy: "all"
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = useQuery(ADVISOR_SESSION_BOOK_EXPERT, t2);
  let t3;
  if ($[5] !== query.data) {
    t3 = toSessionBookExpert(query.data);
    $[5] = query.data;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const expert = t3;
  const t4 = query.loading && !expert;
  let t5;
  if ($[7] !== expert || $[8] !== query.error || $[9] !== t4) {
    t5 = {
      expert,
      loading: t4,
      error: query.error
    };
    $[7] = expert;
    $[8] = query.error;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};